import type { Phien } from './kieu';

export const FBMAX_TOKEN_KEY = 'token';
export const FBMAX_TENANT_KEY = 'fbmax_current_tenant';

/**
 * Timestamp tracking session clearance to protect against stale logout/reload races.
 */
let sessionRevocationTimestamp = 0;

export function markSessionRevoked(): void {
  sessionRevocationTimestamp = Date.now();
}

export function isSessionRevoked(): boolean {
  return sessionRevocationTimestamp > 0;
}

export function resetSessionRevocation(): void {
  sessionRevocationTimestamp = 0;
}

/**
 * Safely extracts tenantId claim from JWT token payload string.
 *
 * NOTE: This is strictly an unverified claim extractor (base64 decode only).
 * It does NOT perform cryptographic signature verification. Authoritative
 * cryptographic verification is performed exclusively by the server (/api/session).
 */
export function parseTokenTenantId(token: string): string | null {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = typeof atob === 'function'
      ? decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
      : typeof Buffer !== 'undefined'
      ? Buffer.from(base64, 'base64').toString('utf8')
      : null;
    if (!json) return null;
    const payload = JSON.parse(json);
    return payload && typeof payload.tenantId === 'string' ? payload.tenantId.trim() : null;
  } catch {
    return null;
  }
}

export interface RehydrateOptions {
  requestTimestamp?: number;
  force?: boolean;
}

/**
 * Rehydrates session token from verified /api/session response into standard localStorage:
 * 1. Data must originate from a verified server session (ok: true, token, user.tenantId).
 * 2. Token payload claim must match session user tenantId (SEC-01).
 * 3. Rejects stale in-flight requests that completed after logout was initiated.
 * 4. Cleans old tenant credentials on tenant switch.
 * 5. Never logs raw tokens to console.
 * 6. Dispatches companion bridge notifications without raw console exposure.
 */
export function rehydrateSessionToken(session: Phien | null | undefined, options?: RehydrateOptions): boolean {
  if (!session || !session.ok || !session.token || !session.user || !session.user.tenantId) {
    return false;
  }

  // Stale logout / reload race protection: reject if session was revoked prior to this completion
  if (sessionRevocationTimestamp > 0 && !options?.force) {
    if (options?.requestTimestamp && options.requestTimestamp <= sessionRevocationTimestamp) {
      return false;
    }
    if (!options?.requestTimestamp) {
      return false;
    }
  }

  const rawToken = String(session.token).trim();
  const sessionTenant = String(session.user.tenantId).trim();
  if (!rawToken || !sessionTenant) return false;

  // Verify tenant identity consistency against payload claims
  const tokenTenant = parseTokenTenantId(rawToken);
  if (tokenTenant && tokenTenant !== sessionTenant) {
    return false;
  }

  try {
    const previousTenant = localStorage.getItem(FBMAX_TENANT_KEY) || localStorage.getItem('tenantId');
    if (previousTenant && previousTenant !== sessionTenant) {
      localStorage.removeItem(FBMAX_TOKEN_KEY);
      localStorage.removeItem('tenantId');
      localStorage.removeItem(FBMAX_TENANT_KEY);
    }

    localStorage.setItem(FBMAX_TENANT_KEY, sessionTenant);
    localStorage.setItem('tenantId', sessionTenant);

    const currentToken = localStorage.getItem(FBMAX_TOKEN_KEY);
    if (currentToken !== rawToken) {
      localStorage.setItem(FBMAX_TOKEN_KEY, rawToken);
    }

    // Reset revocation state since this is a verified newer active session
    sessionRevocationTimestamp = 0;

    // Trigger companion configuration bridge
    try {
      window.dispatchEvent(new StorageEvent('storage', { key: FBMAX_TOKEN_KEY, newValue: rawToken }));
      window.dispatchEvent(new CustomEvent('fbmax-token-updated', { detail: { tenantId: sessionTenant } }));
    } catch {}

    return true;
  } catch {
    return false;
  }
}

/**
 * Clears credentials on logout or rejected session and marks revocation timestamp.
 */
export function clearSessionCredentials(): void {
  markSessionRevoked();
  try {
    localStorage.removeItem(FBMAX_TOKEN_KEY);
    localStorage.removeItem('tenantId');
    localStorage.removeItem(FBMAX_TENANT_KEY);

    try {
      window.dispatchEvent(new StorageEvent('storage', { key: FBMAX_TOKEN_KEY, newValue: null }));
      window.dispatchEvent(new CustomEvent('fbmax-token-updated', { detail: { tenantId: null } }));
    } catch {}
  } catch {}
}
